import {
  FormLabel,
  InputAdornment,
  TextField as MuiTextField,
  TextFieldProps as MuiTextFieldProps,
} from '@material-ui/core';
import { withStyles, WithStyles } from '@material-ui/core/styles';
import cx from 'classnames';
import { MouseEvent, ReactNode, useEffect, useState } from 'react';

import { t } from '@core/i18n';
import { FieldHelperText } from '@shared/components/field-helper-text';
import { Tooltip } from '@shared/components/tooltip';
import { WarningIcon } from '@shared/icons/warning';
import { colours } from '@shared/theme/colours';
import { InputSize } from '@shared/types/common/input';
import { isInvalidNumericInput, parseNumericInput, processDecimal } from '@shared/utils/number';

import { styles } from './NumberField.styles';

interface NumberFieldConfig {
  decimalPlaces?: number;
  max?: number;
  min?: number;
  roundIfWhole?: boolean;
  setMinIfEmpty?: boolean;
}

const processValue = (value: string, config: NumberFieldConfig): string => {
  // Convert to fixed decimal place, if supplied value is not integer
  const parsedValue = processDecimal(value, {
    decimalPlaces: config.decimalPlaces,
    roundIfWhole: config.roundIfWhole,
  });
  const numericValue = parseNumericInput(parsedValue);
  // console.log('processValue. value: ', value, 'parsedValue: ', parsedValue);

  // Return the min value if the value is smaller
  if (
    typeof config.min === 'number' &&
    // Set min if empty
    ((config.setMinIfEmpty && parsedValue === '') ||
      // Set min if value lower than min
      (numericValue !== null && numericValue < config.min))
  ) {
    return config.min.toString();
  }

  // Return the min value if the value is larger
  if (typeof config.max === 'number' && numericValue !== null && numericValue > config.max) {
    return config.max.toString();
  }

  return parsedValue;
};

// Unparseable entries are flagged separately via isInvalidNumericInput, so they count as in range here.
const validateValue = (value: string, config: NumberFieldConfig): boolean => {
  const numericValue = parseNumericInput(value);

  if (numericValue === null) {
    return true;
  }
  if (typeof config.min === 'number' && numericValue < config.min) {
    return false;
  }
  if (typeof config.max === 'number' && numericValue > config.max) {
    return false;
  }

  return true;
};

type Classes = WithStyles<typeof styles>;

export interface NumberFieldProps
  extends Omit<MuiTextFieldProps, 'classes' | 'margin' | 'onChange' | 'onWheel' | 'placeholder' | 'size'>,
    Classes {
  endIcon?: ReactNode;
  errorText?: string;
  decimalPlaces?: number;
  infoText?: string;
  label?: React.ReactNode;
  min?: number;
  max?: number;
  placeholder?: string;
  required?: boolean;
  roundIfWhole?: boolean;
  setMinIfEmpty?: boolean;
  size?: InputSize;
  startIcon?: React.ReactNode;
  step?: number;
  value?: number | string;
  warning?: boolean;
  warningText?: string;
  onChange: (value: string) => void;
  onValidityChange?: (valid: boolean) => void;
}

// A press on a prefix or suffix, such as a currency sign, keeps focus in the field's input rather than taking it away
const focusInputFromAdornment = (event: MouseEvent<HTMLElement>) => {
  const input = event.currentTarget.parentElement?.querySelector('input');

  if (!input || input.disabled) return;

  event.preventDefault();
  input.focus();
};

const NumberFieldComponent: React.FC<NumberFieldProps> = ({
  className,
  classes,
  decimalPlaces,
  disabled,
  endIcon,
  error,
  errorText,
  fullWidth,
  infoText,
  inputProps,
  InputProps,
  label,
  min,
  max,
  placeholder,
  required,
  roundIfWhole = false,
  setMinIfEmpty = false,
  size = InputSize.mediumLegacy,
  startIcon,
  step,
  type,
  value,
  warning,
  warningText,
  onBlur,
  onChange,
  onFocus,
  onKeyDown,
  onValidityChange,
  ...otherProps
}) => {
  const {
    root: rootClass,
    rootFullWidth: rootFullWidthClass,
    labelRoot: labelRootClass,
    input: inputClass,
    label: labelClass,
    labelError: labelErrorClass,
    helperText: helperTextClass,
    adornmentText: adornmentTextClass,
    endAdornment: endAdornmentClass,
  } = classes;

  // State
  const [localValue, setLocalValue] = useState<string>('');
  const [localError, setLocalError] = useState<boolean>(false);
  const [localInvalid, setLocalInvalid] = useState<boolean>(false);
  const [reverted, setReverted] = useState<boolean>(false);

  const processConfig: NumberFieldConfig = { decimalPlaces, max, min, roundIfWhole, setMinIfEmpty };

  // Style text suffixes/prefixes as a muted unit; leave supplied icon elements untouched.
  const renderAdornment = (content: ReactNode) =>
    typeof content === 'string' ? <span className={adornmentTextClass}>{content}</span> : content;

  const committedValue = typeof value === 'number' ? value.toString() : typeof value === 'string' ? value : '';

  // Update local value when parent value changes. A revert warning only describes the entry the
  // user just made, so a parent-driven value change makes it stale.
  useEffect(() => {
    setLocalValue(committedValue);
    setReverted(false);
  }, [committedValue]);

  useEffect(() => {
    setReverted(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- Clear the revert warning whenever the field is disabled or re-enabled
  }, [disabled]);

  // A rejected entry leaves a warning in the field until the next focus or edit, so the user can
  // see that what they typed was dropped without the layout moving. Any consumer suffix, whether
  // passed as endIcon or as InputProps.endAdornment, stays beside it.
  const endAdornment =
    reverted || endIcon ? (
      <>
        {InputProps?.endAdornment}
        <InputAdornment classes={{ root: endAdornmentClass }} position="end" onMouseDown={focusInputFromAdornment}>
          {endIcon && renderAdornment(endIcon)}
          {reverted && (
            <Tooltip placement="left" title={t('invalid_number_value_reverted')}>
              <WarningIcon color={colours.warning[500]} />
            </Tooltip>
          )}
        </InputAdornment>
      </>
    ) : (
      InputProps?.endAdornment
    );

  const textField = (
    <MuiTextField
      disabled={disabled}
      error={localError || error}
      fullWidth={fullWidth}
      placeholder={placeholder || undefined}
      type="text"
      value={localValue}
      variant="outlined"
      InputProps={{
        ...InputProps,
        classes: { ...InputProps?.classes, root: cx(inputClass, classes[size], InputProps?.classes?.root) },
        endAdornment,
        startAdornment: startIcon ? (
          <InputAdornment position="start" onMouseDown={focusInputFromAdornment}>
            {renderAdornment(startIcon)}
          </InputAdornment>
        ) : (
          InputProps?.startAdornment
        ),
      }}
      inputProps={{ ...inputProps, inputMode: 'decimal', min, max, step }}
      onBlur={(e) => {
        e.persist();

        // Unparseable entry is never committed: restore the committed value so the input and the
        // model agree, rather than leaving junk on screen that the form cannot see.
        if (isInvalidNumericInput(e.target.value)) {
          setLocalError(false);
          setLocalInvalid(false);
          setLocalValue(committedValue);
          setReverted(true);
          onValidityChange?.(true);

          if (onBlur) {
            // Consumers reading the event must see the committed value, never the rejected entry.
            e.target.value = committedValue;
            onBlur(e);
          }

          return;
        }

        const parsedValue = parseNumericInput(e.target.value);
        const processedValue = processValue(parsedValue === null ? '' : parsedValue.toString(), processConfig);
        // console.log('processedValue: ', processedValue);

        setLocalError(false);
        setLocalValue(processedValue);
        onChange(processedValue);

        if (onBlur) {
          onBlur(e);
        }
      }}
      onChange={(e) => {
        e.persist();

        const invalid = isInvalidNumericInput(e.target.value);

        setLocalError(invalid || !validateValue(e.target.value, processConfig));
        setLocalInvalid(invalid);
        setLocalValue(e.target.value);
        setReverted(false);
        onValidityChange?.(!invalid);
      }}
      onFocus={(e) => {
        setReverted(false);

        if (onFocus) {
          onFocus(e);
        }
      }}
      onKeyDown={(e) => {
        // Enter must not submit or blur while the entry is unparseable; the user has to fix or leave it.
        if (e.key === 'Enter' && localInvalid) {
          e.preventDefault();
          e.stopPropagation();

          return;
        }

        if (onKeyDown) {
          onKeyDown(e);
        }
      }}
      {...otherProps}
    />
  );

  return (
    <div className={cx(rootClass, { [rootFullWidthClass]: fullWidth }, className)}>
      {label && (
        <FormLabel
          classes={{ root: cx(labelRootClass, labelClass), error: labelErrorClass }}
          component="legend"
          error={error}
        >
          {label}
        </FormLabel>
      )}
      {textField}
      <FieldHelperText
        classes={{ root: helperTextClass }}
        error={error}
        errorText={errorText}
        infoText={infoText}
        warning={warning}
        warningText={warningText}
      />
    </div>
  );
};

export const NumberField = withStyles(styles)(NumberFieldComponent);
